import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Satellite, Plus, Trash2, PlayCircle, ExternalLink, X, KeyRound } from "lucide-react";

const VERDICT_COLOR = {
  clean: "#00F0FF", suspicious: "#FFC542", malicious: "#FF2E63",
  unknown: "#8E9196", error: "#FF6B35", config_missing_key: "#FF6B35",
  config_missing_url: "#FF6B35", not_supported: "#52525B", not_found: "#8E9196",
};

export default function AdminIntelFeeds() {
  const [feeds, setFeeds] = useState([]);
  const [adapters, setAdapters] = useState({});
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [testTarget, setTestTarget] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [f, a] = await Promise.all([
        api.get("/admin/intel-feeds"),
        api.get("/admin/intel-feeds/adapters"),
      ]);
      setFeeds(f.data.feeds || []);
      setAdapters(a.data.adapters || {});
    } catch (e) {
      toast.error("Failed to load intel feeds");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function toggleEnabled(f) {
    try {
      await api.patch(`/admin/intel-feeds/${f.id}`, { enabled: !f.enabled });
      toast.success(`${f.name} ${!f.enabled ? "enabled" : "disabled"}`);
      await load();
    } catch (e) {
      toast.error("Update failed");
    }
  }

  async function del(f) {
    if (!window.confirm(`Delete feed "${f.name}"?`)) return;
    try {
      await api.delete(`/admin/intel-feeds/${f.id}`);
      toast.success("Feed deleted");
      await load();
    } catch (e) {
      toast.error("Delete failed");
    }
  }

  return (
    <div className="min-h-screen bg-[#060606] text-white p-8" data-testid="intel-feeds-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Satellite className="w-5 h-5 text-[#00F0FF]" strokeWidth={1.75} />
          <h1 className="font-display font-black text-3xl tracking-tight">INTEL FEEDS</h1>
        </div>
        <p className="text-sm text-[#8E9196] max-w-2xl mb-6">
          Plug in threat-intel sources beyond VirusTotal. Enable a built-in adapter (OTX, ThreatFox, urlscan.io) or wire up any REST endpoint via <span className="text-[#00F0FF]">Custom HTTP</span>. Every enabled feed is queried in parallel on every IOC lookup.
        </p>

        <div className="flex justify-end mb-4">
          <button
            onClick={() => setShowNew(true)}
            data-testid="intel-new-btn"
            className="flex items-center gap-2 px-4 py-2 bg-[#00F0FF] text-black text-[10px] font-mono uppercase tracking-widest rounded-sm hover:bg-white transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add feed
          </button>
        </div>

        <div className="border border-white/10 rounded-sm bg-[#0a0a0a]" data-testid="intel-feed-list">
          <div className="grid grid-cols-[1.5fr_120px_100px_140px_120px_120px] px-4 py-2 border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">
            <div>Name</div>
            <div>Adapter</div>
            <div>Enabled</div>
            <div>Last status</div>
            <div>Last used</div>
            <div></div>
          </div>
          {loading && <div className="p-6 text-xs text-[#52525B] font-mono">Loading…</div>}
          {!loading && feeds.length === 0 && (
            <div className="p-6 text-xs text-[#52525B] font-mono">
              No feeds configured yet. Click <span className="text-white">Add feed</span> to start with a free adapter like ThreatFox or urlscan.io.
            </div>
          )}
          {feeds.map((f) => {
            const a = adapters[f.adapter] || {};
            return (
              <div key={f.id}
                   className="grid grid-cols-[1.5fr_120px_100px_140px_120px_120px] items-center px-4 py-3 border-b border-white/[0.04] last:border-0 text-sm"
                   data-testid={`intel-feed-row-${f.id}`}>
                <div>
                  <div className="font-medium">{f.name}</div>
                  <div className="text-[10px] font-mono text-[#8E9196]">
                    Supports: {(f.supports || a.supports || []).join(", ")}
                  </div>
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF]">{a.label || f.adapter}</div>
                <div>
                  <button
                    onClick={() => toggleEnabled(f)}
                    data-testid={`intel-toggle-${f.id}`}
                    className={`px-2 py-1 rounded-sm text-[10px] font-mono uppercase tracking-widest ${
                      f.enabled ? "bg-[#00F0FF]/20 text-[#00F0FF] border border-[#00F0FF]/40" : "border border-white/10 text-[#8E9196]"
                    }`}
                  >
                    {f.enabled ? "ON" : "OFF"}
                  </button>
                </div>
                <div>
                  {f.last_status ? (
                    <span className="text-[10px] font-mono uppercase tracking-widest"
                          style={{ color: VERDICT_COLOR[f.last_status] || "#8E9196" }}>
                      {f.last_status}
                    </span>
                  ) : (
                    <span className="text-[10px] font-mono text-[#52525B]">—</span>
                  )}
                </div>
                <div className="text-[10px] font-mono text-[#8E9196]">
                  {f.last_used_at ? new Date(f.last_used_at).toLocaleString() : "never"}
                </div>
                <div className="flex justify-end gap-1">
                  <button
                    onClick={() => setTestTarget(f)}
                    data-testid={`intel-test-${f.id}`}
                    title="Test this feed against an indicator"
                    className="p-1.5 border border-white/10 hover:border-[#B57BFF] hover:text-[#B57BFF] rounded-sm text-[#8E9196] transition-colors"
                  >
                    <PlayCircle className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => del(f)}
                    data-testid={`intel-delete-${f.id}`}
                    title="Delete feed"
                    className="p-1.5 border border-white/10 hover:border-[#FF2E63] hover:text-[#FF2E63] rounded-sm text-[#8E9196] transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showNew && (
        <NewFeedModal adapters={adapters} onCancel={() => setShowNew(false)} onCreated={async () => { setShowNew(false); await load(); }} />
      )}
      {testTarget && (
        <TestModal feed={testTarget} onClose={() => setTestTarget(null)} />
      )}
    </div>
  );
}

function NewFeedModal({ adapters, onCancel, onCreated }) {
  const [adapter, setAdapter] = useState("threatfox");
  const [form, setForm] = useState({
    name: "", api_key: "", url_template: "", method: "GET", verdict_keyword: "malicious",
    header_name: "X-API-Key", param_name: "apikey", auth_mode: "header",
  });
  const [saving, setSaving] = useState(false);
  const a = adapters[adapter] || {};

  async function submit() {
    if (!form.name.trim()) { toast.error("Feed name required"); return; }
    const auth_config = {};
    if (adapter === "custom_http") {
      auth_config.mode = form.auth_mode;
      if (form.api_key) auth_config.api_key = form.api_key;
      if (form.auth_mode === "header") auth_config.header_name = form.header_name;
      if (form.auth_mode === "query") auth_config.param_name = form.param_name;
    } else if (form.api_key) {
      auth_config.api_key = form.api_key;
    }
    setSaving(true);
    try {
      await api.post("/admin/intel-feeds", {
        name: form.name.trim(),
        adapter,
        enabled: true,
        auth_config,
        url_template: adapter === "custom_http" ? form.url_template.trim() : undefined,
        method: form.method,
        verdict_keyword: form.verdict_keyword,
      });
      toast.success("Feed added");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-8" onClick={onCancel}>
      <div className="bg-[#0a0a0a] border border-[#00F0FF]/60 rounded-sm p-6 max-w-xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="intel-new-modal">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] mb-1">Add intel feed</div>
            <div className="font-display font-black text-2xl">Pick an adapter</div>
          </div>
          <button onClick={onCancel} className="text-[#8E9196] hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#52525B] mb-2">Adapter</div>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(adapters).map(([key, val]) => (
                <button
                  key={key}
                  onClick={() => setAdapter(key)}
                  data-testid={`intel-adapter-${key}`}
                  className={`text-left px-3 py-2 border rounded-sm transition-colors ${
                    adapter === key ? "border-[#00F0FF] bg-[#00F0FF]/[0.05]" : "border-white/10 hover:border-white/30"
                  }`}
                >
                  <div className="text-sm text-white">{val.label}</div>
                  <div className="text-[10px] font-mono text-[#8E9196] mt-0.5">{val.description}</div>
                  {val.requires_key && <div className="text-[9px] font-mono uppercase text-[#FFC542] mt-1">API key required</div>}
                </button>
              ))}
            </div>
            {a.signup_url && (
              <a href={a.signup_url} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1 mt-2 text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] hover:underline">
                Get {a.label} API key <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>

          <Field label="Feed name" value={form.name} onChange={(v) => setForm({ ...form, name: v })}
                 testid="intel-new-name" placeholder="e.g. Company OTX prod" />

          {a.requires_key || adapter === "custom_http" ? (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#52525B] mb-1 flex items-center gap-1">
                <KeyRound className="w-3 h-3" /> API key {a.requires_key ? "(required)" : "(optional)"}
              </div>
              <input type="password" value={form.api_key}
                     onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                     data-testid="intel-new-apikey"
                     className="w-full bg-black border border-white/10 focus:border-[#00F0FF] rounded-sm px-3 py-2 text-sm outline-none text-white font-mono" />
            </div>
          ) : null}

          {adapter === "custom_http" && (
            <>
              <Field label={<>URL template <span className="text-[#52525B] normal-case">(use {'{indicator}'} and {'{kind}'} placeholders)</span></>}
                     value={form.url_template} onChange={(v) => setForm({ ...form, url_template: v })}
                     testid="intel-new-url" placeholder="https://intel.example.com/lookup?ioc={indicator}" />
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#52525B] mb-1">Auth style</div>
                <div className="flex gap-1">
                  {["none", "header", "query"].map((m) => (
                    <button key={m} onClick={() => setForm({ ...form, auth_mode: m })}
                            data-testid={`intel-new-authmode-${m}`}
                            className={`px-3 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm border transition-colors ${
                              form.auth_mode === m ? "bg-[#00F0FF] text-black border-[#00F0FF]" : "text-[#8E9196] border-white/10 hover:text-white"
                            }`}>{m}</button>
                  ))}
                </div>
              </div>
              {form.auth_mode === "header" && (
                <Field label="Header name" value={form.header_name} onChange={(v) => setForm({ ...form, header_name: v })} testid="intel-new-header" placeholder="X-API-Key" />
              )}
              {form.auth_mode === "query" && (
                <Field label="Query param name" value={form.param_name} onChange={(v) => setForm({ ...form, param_name: v })} testid="intel-new-param" placeholder="apikey" />
              )}
              <Field label="Verdict keyword (substring in response body → marks malicious)"
                     value={form.verdict_keyword} onChange={(v) => setForm({ ...form, verdict_keyword: v })}
                     testid="intel-new-keyword" placeholder="malicious" />
            </>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel} className="px-3 py-2 border border-white/10 text-[10px] font-mono uppercase tracking-widest text-[#8E9196] hover:text-white rounded-sm">Cancel</button>
          <button onClick={submit} disabled={saving}
                  data-testid="intel-new-submit"
                  className="px-4 py-2 bg-[#00F0FF] text-black text-[10px] font-mono uppercase tracking-widest rounded-sm hover:bg-white disabled:opacity-40">
            {saving ? "Saving…" : "Add feed"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#52525B] mb-1">{label}</div>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder}
             className="w-full bg-black border border-white/10 focus:border-[#00F0FF] rounded-sm px-3 py-2 text-sm outline-none text-white placeholder:text-[#52525B]" />
    </div>
  );
}

function TestModal({ feed, onClose }) {
  const [indicator, setIndicator] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  async function run() {
    if (!indicator.trim()) return;
    setRunning(true);
    try {
      const r = await api.post(`/admin/intel-feeds/${feed.id}/test`, { indicator: indicator.trim() });
      setResult(r.data.result);
    } catch (e) {
      toast.error("Lookup failed");
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-8" onClick={onClose}>
      <div className="bg-[#0a0a0a] border border-[#B57BFF]/60 rounded-sm p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="intel-test-modal">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#B57BFF] mb-1">Test feed</div>
            <div className="font-display font-black text-2xl">{feed.name}</div>
          </div>
          <button onClick={onClose} className="text-[#8E9196] hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex gap-2 mb-4">
          <input value={indicator} onChange={(e) => setIndicator(e.target.value)}
                 placeholder="e.g. 8.8.8.8, evil.com, hash…"
                 data-testid="intel-test-indicator"
                 className="flex-1 bg-black border border-white/10 focus:border-[#B57BFF] rounded-sm px-3 py-2 text-sm outline-none text-white" />
          <button onClick={run} disabled={running || !indicator.trim()}
                  data-testid="intel-test-run"
                  className="px-4 py-2 bg-[#B57BFF] text-black text-[10px] font-mono uppercase tracking-widest rounded-sm hover:bg-white disabled:opacity-40">
            {running ? "Looking…" : "Look up"}
          </button>
        </div>
        {result && (
          <div className="bg-black border border-white/10 rounded-sm p-4" data-testid="intel-test-result">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-mono uppercase tracking-widest"
                    style={{ color: VERDICT_COLOR[result.verdict] || "#8E9196" }}>
                {result.verdict}
              </span>
              <span className="text-[10px] font-mono text-[#52525B]">· {result.source}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs font-mono">
              <div><span className="text-[#8E9196]">Malicious:</span> {result.malicious}</div>
              <div><span className="text-[#8E9196]">Suspicious:</span> {result.suspicious}</div>
              <div><span className="text-[#8E9196]">Harmless:</span> {result.harmless}</div>
              <div><span className="text-[#8E9196]">Reputation:</span> {result.reputation}</div>
            </div>
            {result.detections?.length > 0 && (
              <div className="mt-2 text-xs">
                <div className="text-[#8E9196] font-mono">Detections:</div>
                <div className="text-white">{result.detections.slice(0, 5).join(", ")}</div>
              </div>
            )}
            {result.raw && (
              <details className="mt-3">
                <summary className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] cursor-pointer hover:text-white">Raw response</summary>
                <pre className="mt-2 text-[10px] font-mono text-[#8E9196] bg-[#0a0a0a] p-2 rounded-sm overflow-x-auto">{JSON.stringify(result.raw, null, 2)}</pre>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
